import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface BankTransactionLog {
  id: number;
  companyId: string;
  transactionId: number;
  bankAccountId: number;
  bankSubAccountId?: number;
  type: string;
  transactionType?: string;
  amount: number;
  description?: string;
  currency: string;
  date: string;
  balanceBefore: number;
  balanceAfter: number;
  paymentMethod?: string;
  documentNo?: string;
  screenId?: string;
  screenName?: string;
  relatedBankAccountId?: number;
  relatedBankSubAccountId?: number;
  relatedAccountType?: string;
  relatedAccountId?: string;
  relatedAccountName?: string;
  authCode?: string;
  batchNo?: string;
  cardType?: string;
  fiscalTransactionId?: string;
  maskedCardNumber?: string;
  changedField: string;
  reason?: string;
  deletedBy?: string;
  deletedAt: string;
  originalCreatedByEmail?: string;
  originalCreatedAt: string;
  isActive: boolean;
}

interface BankTransactionLogsModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
}

// Para birimi sembolünü döndür
const getCurrencySymbol = (currency: string): string => {
  switch (currency) {
    case 'TRY': return '₺';
    case 'USD': return '$';
    case 'EUR': return '€';
    case 'GBP': return '£';
    case 'JPY': return '¥';
    case 'CHF': return 'Fr.';
    case 'AUD': return 'A$';
    case 'CAD': return 'C$';
    case 'CNY': return '¥';
    default: return currency;
  }
};

const BankTransactionLogsModal: React.FC<BankTransactionLogsModalProps> = ({ open, onClose, companyId }) => {
  const { t, i18n } = useTranslation('accounting');
  const locale = i18n.language?.toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR';
  const [logs, setLogs] = useState<BankTransactionLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getTransactionTypeLabel = (type?: string): string => {
    if (!type) return '-';
    const upperType = type.toUpperCase();
    switch (upperType) {
      case 'DEPOSIT': return t('bank.transactionReport.types.deposit');
      case 'WITHDRAWAL':
      case 'WITHDRAW': return t('bank.transactionReport.types.withdraw');
      case 'CASH_TRANSFER': return t('bank.transactionReport.types.transfer');
      case 'QUICK_SALE': return t('bank.transactionReport.types.quickSale');
      case 'COLLECTION': return t('bank.transactionReport.types.collection');
      case 'PAYMENT': return t('bank.transactionReport.types.payment');
      default: return type;
    }
  };

  // Modal açıldığında logları yükle
  useEffect(() => {
    if (open && companyId) {
      loadLogs();
    }
  }, [open, companyId]);

  const loadLogs = async () => {
    if (!companyId) {
      setError(t('bank.transactionReport.errors.companyNotFound'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('📋 Banka işlem logları yükleniyor...');

      const response = await accountingApi.get(ACCOUNTING_API.BANK_TRANSACTION_LOGS_BY_COMPANY);
      const logsData = response.data.data || response.data;

      console.log('✅ Loglar yüklendi:', logsData);
      setLogs(logsData);
    } catch (error: any) {
      console.error('❌ Loglar yüklenirken hata:', error);
      setError(error.message || t('bank.logsModal.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Tarih formatla
  const formatDate = (dateString: string): string => {
    try {
      const date = parseISO(dateString);
      return format(date, 'dd.MM.yyyy HH:mm:ss');
    } catch {
      return dateString;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">{t('bank.logsModal.title')}</Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label={t('bank.common.close')}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>{t('bank.logsModal.loading')}</Typography>
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : logs.length === 0 ? (
          <Alert severity="info">{t('bank.logsModal.empty')}</Alert>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                    {t('bank.logsModal.columns.deletedAt')}
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                    {t('bank.logsModal.columns.deletedBy')}
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                    {t('bank.logsModal.columns.transactionDate')}
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                    {t('bank.logsModal.columns.transactionType')}
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                    {t('bank.logsModal.columns.amount')}
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                    {t('bank.logsModal.columns.description')}
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                    {t('bank.logsModal.columns.paymentMethod')}
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                    {t('bank.logsModal.columns.documentNo')}
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                    {t('bank.logsModal.columns.balance')}
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                    {t('bank.logsModal.columns.reason')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {formatDate(log.deletedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{log.deletedBy || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(log.date)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getTransactionTypeLabel(log.transactionType)}
                        size="small"
                        color={log.type === 'income' ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={log.type === 'income' ? 'success.main' : 'error.main'}
                      >
                        {log.amount.toLocaleString(locale)} {getCurrencySymbol(log.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {log.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{log.paymentMethod || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{log.documentNo || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('bank.logsModal.before')}: {log.balanceBefore.toLocaleString(locale)} {getCurrencySymbol(log.currency)}
                        </Typography>
                        <br />
                        <Typography variant="caption" color="text.secondary">
                          {t('bank.logsModal.after')}: {log.balanceAfter.toLocaleString(locale)} {getCurrencySymbol(log.currency)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {log.reason || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BankTransactionLogsModal;

